import {
    IQueryContext,
    IQueryContextDatasourceColumn,
} from 'src/app/services/query-context/models/IQueryContext';
import { BQSqlFunctionHelper } from 'src/app/utils/datasource-sql-map/bq-sql-function-helper';
import {
    DatasourceColumnId,
    ICompiledSQLTokens,
    IDatasourceSQLMap,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { DatePickerSqlTokensCompiler } from 'src/app/utils/datasource-sql-map/date-picker-sql-tokens-compiler';
import {
    multipleSelectComponentCompileSQLTokens,
    segmentContainerIdCompileSQLTokens,
} from 'src/app/utils/datasource-sql-map/utils';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { buildSplitGroupSQL, buildWhereSQL } from 'src/app/utils/query/sql';
import { sqlDateTrunc } from 'src/app/utils/sql';

export class BIVizDashboardAdEvents {
    // getter for sqlMap
    public static get sqlMap() {
        const sqlMap: IDatasourceSQLMap = {
            dataset: 'bi_viz',
            tableId: 'dashboard_ad_events',
            columns: {
                AD_PLATFORM: {
                    columnId: 'AD_PLATFORM',
                    columnSQL: 'ad_platform',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'ad_platform',
                            state
                        );
                    },
                },
                AD_NAME: {
                    columnId: 'AD_NAME',
                    columnSQL: 'ad_name',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'ad_name',
                            state
                        );
                    },
                },
                AD_CONSENT: {
                    columnId: 'AD_CONSENT',
                    columnSQL: 'ad_consent',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'ad_consent',
                            state
                        );
                    },
                },
                DAILY_AD_CONSENT: {
                    columnId: 'DAILY_AD_CONSENT',
                    columnSQL: 'daily_ad_consent',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'daily_ad_consent',
                            state
                        );
                    },
                },
                ATTRIBUTION_AD: {
                    columnId: 'ATTRIBUTION_AD',
                    columnSQL: 'player_dim.attribution.unified_ad',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.attribution.unified_ad',
                            state
                        );
                    },
                },
                ATTRIBUTION_ADSET: {
                    columnId: 'ATTRIBUTION_ADSET',
                    columnSQL: 'player_dim.attribution.unified_adset',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.attribution.unified_adset',
                            state
                        );
                    },
                },
                ATTRIBUTION_CAMPAIGN_NAME: {
                    columnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                    columnSQL: 'player_dim.attribution.unified_campaign',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.attribution.unified_campaign',
                            state
                        );
                    },
                },
                ATTRIBUTION_TRAFFIC_SOURCE: {
                    columnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
                    columnSQL: 'player_dim.attribution.unified_traffic',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.attribution.unified_traffic',
                            state
                        );
                    },
                },
                ATTRIBUTION_NETWORK_NAME: {
                    columnId: 'ATTRIBUTION_NETWORK_NAME',
                    columnSQL: 'player_dim.attribution.unified_network',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.attribution.unified_network',
                            state
                        );
                    },
                },
                FIREBASE_APP_STORE: {
                    columnId: 'FIREBASE_APP_STORE',
                    columnSQL: 'player_dim.revenue_sessions.first_store',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.revenue_sessions.first_store',
                            state
                        );
                    },
                },
                FIREBASE_COUNTRY_EUROPEAN_UNION: {
                    columnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                    columnSQL: 'player_dim.geo.first_geo.country_european_union',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.geo.first_geo.country_european_union',
                            state
                        );
                    },
                },
                FIREBASE_COUNTRY_TIER: {
                    columnId: 'FIREBASE_COUNTRY_TIER',
                    columnSQL: 'player_dim.geo.first_geo.country_tier_label',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.geo.first_geo.country_tier_label',
                            state
                        );
                    },
                },
                FIREBASE_COUNTRY: {
                    columnId: 'FIREBASE_COUNTRY',
                    columnSQL: 'player_dim.geo.first_geo.unified_country',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.geo.first_geo.unified_country',
                            state
                        );
                    },
                },
                FIREBASE_FIRST_OPEN_DATE: {
                    columnId: 'FIREBASE_FIRST_OPEN_DATE',
                    columnSQL: 'player_dim.revenue_sessions.first_session_date',
                    compileSQLTokens: (state) => {
                        return DatePickerSqlTokensCompiler.compileTokens(
                            'player_dim.revenue_sessions.first_session_date',
                            state
                        );
                    },
                },
                ETL_PAYER: {
                    columnId: 'ETL_PAYER',
                    columnSQL: 'player_dim.revenue_sessions.payer_label',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.revenue_sessions.payer_label',
                            state
                        );
                    },
                },
                ETL_REVENUE_BADGE: {
                    columnId: 'ETL_REVENUE_BADGE',
                    columnSQL: 'player_dim.revenue_sessions.revenue_badge',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.revenue_sessions.revenue_badge',
                            state
                        );
                    },
                },
                FIREBASE_INSTALL_VERSION: {
                    columnId: 'FIREBASE_INSTALL_VERSION',
                    columnSQL: 'player_dim.revenue_sessions.install_version',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'player_dim.revenue_sessions.install_version',
                            state
                        );
                    },
                },
                FIREBASE_INSTALL_VERSION_GROUP_MINOR: {
                    columnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                    columnSQL: `${BQSqlFunctionHelper.getCallSqlStatement(
                        BQSqlFunctionHelper.VERSION_HIERARCHY,
                        ['player_dim.revenue_sessions.install_version']
                    )}.minor`,
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            `${BQSqlFunctionHelper.getCallSqlStatement(
                                BQSqlFunctionHelper.VERSION_HIERARCHY,
                                ['player_dim.revenue_sessions.install_version']
                            )}.minor`,
                            state
                        );
                    },
                },
                ACTIVITY_DATE: {
                    columnId: 'ACTIVITY_DATE',
                    columnSQL: 'date',
                    compileSQLTokens: (state) => {
                        return DatePickerSqlTokensCompiler.compileTokens(
                            'date',
                            state
                        );
                    },
                },
                ACTIVITY_APP_VERSION: {
                    columnId: 'ACTIVITY_APP_VERSION',
                    columnSQL: 'app_version',
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            'app_version',
                            state
                        );
                    },
                },
                ACTIVITY_APP_VERSION_GROUP_MINOR: {
                    columnId: 'ACTIVITY_APP_VERSION_GROUP_MINOR',
                    columnSQL: `${BQSqlFunctionHelper.getCallSqlStatement(
                        BQSqlFunctionHelper.VERSION_HIERARCHY,
                        ['app_version']
                    )}.minor`,
                    compileSQLTokens: (state) => {
                        return multipleSelectComponentCompileSQLTokens(
                            `${BQSqlFunctionHelper.getCallSqlStatement(
                                BQSqlFunctionHelper.VERSION_HIERARCHY,
                                ['app_version']
                            )}.minor`,
                            state
                        );
                    },
                },
                SEGMENT: {
                    columnId: 'SEGMENT',
                    columnSQL: 'segments',
                    compileSQLTokens: segmentContainerIdCompileSQLTokens,
                },
            },
        };
        return sqlMap;
    }

    // compile sql statements specific for this datasource
    public static compileSQLStatements(
        supportedDatasourceColumns: IQueryContextDatasourceColumn[],
        queryContextSettings: IQueryContext['settings'],
        options: { gcpProjectId: GcpProjectId },
        sqlMap: IDatasourceSQLMap = this.sqlMap
    ) {
        const dateTrunc = queryContextSettings.dateAggregation
            ? sqlDateTrunc('date', queryContextSettings.dateAggregation)
            : `date`;

        const adsFlowSpecificColumns: DatasourceColumnId[] = ['AD_PLATFORM', 'AD_NAME'];
        // if adFlowSPecificColumns wtih split applied are present in QueryContext
        const is_in_context_split_ads_flow_specific_columns =
            supportedDatasourceColumns.findIndex(
                (dsCol) =>
                    adsFlowSpecificColumns.includes(dsCol.id) &&
                    dsCol.state.isSplitApplied
            ) != -1;

        const sqlTokensArray: Partial<ICompiledSQLTokens>[] =
            supportedDatasourceColumns.map((dsCol) => {
                return sqlMap.columns[dsCol.id]!.compileSQLTokens!(
                    dsCol.state,
                    options
                );
            });

        // merge tokens into single deduplicated object of type ICompiledSQLTokens
        const sqlTokens: ICompiledSQLTokens =
            mergeSqlTokensArray(sqlTokensArray);

        // adsFlow specfic
        const sqlTokensArrayAdsFlowSpecific: Partial<ICompiledSQLTokens>[] =
            supportedDatasourceColumns
                .filter((dsCol) => adsFlowSpecificColumns.includes(dsCol.id))
                .map((dsCol) => {
                    return sqlMap.columns[dsCol.id]!.compileSQLTokens!(
                        dsCol.state,
                        options
                    );
                });

        // merge tokens into single deduplicated object of type ICompiledSQLTokens
        const sqlTokensAdsFlowSpecific: ICompiledSQLTokens =
            mergeSqlTokensArray(sqlTokensArrayAdsFlowSpecific);

        const adsFlowSplitSelect = buildSplitGroupSQL(
            sqlTokensAdsFlowSpecific.split_group,
            'ads_flow_split_group'
        );
        const adsFlowSplitGroupAlias = adsFlowSplitSelect
            ? 'ads_flow_split_group'
            : '';
        const adsFlowSplitGroupBy = adsFlowSplitSelect
            ? 'ads_flow_split_group, '
            : '';

        // cohort - non adsFlow specfic
        const sqlTokensArrayNonAdsFlowSpecific: Partial<ICompiledSQLTokens>[] =
            supportedDatasourceColumns
                .filter((dsCol) => !adsFlowSpecificColumns.includes(dsCol.id))
                .map((dsCol) => {
                    return sqlMap.columns[dsCol.id]!.compileSQLTokens!(
                        dsCol.state,
                        options
                    );
                });

        // merge tokens into single deduplicated object of type ICompiledSQLTokens
        const sqlTokensNonAdsFlowSpecific: ICompiledSQLTokens =
            mergeSqlTokensArray(sqlTokensArrayNonAdsFlowSpecific);

        const cohortSplitSelect = buildSplitGroupSQL(
            sqlTokensNonAdsFlowSpecific.split_group,
            'cohort_split_group'
        );
        const cohortSplitGroupAlias = cohortSplitSelect
            ? 'cohort_split_group'
            : '';
        const cohortSplitGroupBy = cohortSplitSelect
            ? 'cohort_split_group, '
            : '';
        const cohortWhere = buildWhereSQL(sqlTokensNonAdsFlowSpecific.where);

        // CUSTOM FILTER FOR THIS TABLE
        // sqlTokens.where.push(`validation_flag IS NULL`);

        const splitSelect: string = buildSplitGroupSQL(sqlTokens.split_group);
        const splitGroupBy = splitSelect ? 'split_group, ' : '';
        const countStarOrDistinctProperId = splitSelect
            ? 'COUNT(DISTINCT proper_id)'
            : 'COUNT(*)';
        const unnestDatasourceColumn =
            sqlTokens.unnestDatasourceColumn.join('\n');
        const where: string = buildWhereSQL(sqlTokens.where);
        const join: string = sqlTokens.join.join('\n');
        const windowTotal = splitGroupBy ? 'PARTITION BY split_group' : '';
        const windowRunning = splitGroupBy
            ? 'PARTITION BY split_group ORDER BY day_no'
            : 'ORDER BY day_no';

        const splitSelectConcatCohortAdsFlow = (() => {
            const areBothPresent =
                adsFlowSplitGroupBy != '' && cohortSplitGroupBy != '';

            if (areBothPresent) {
                return `CONCAT(${adsFlowSplitGroupAlias}, ', ', ${cohortSplitGroupAlias}) AS split_group,`;
            }
            // if any of them is present then return it
            if (adsFlowSplitGroupBy != '' || cohortSplitGroupBy != '') {
                // remove trailing comma
                return `${adsFlowSplitGroupAlias}${cohortSplitGroupAlias} AS split_group,`;
            }
            return '';
        })();

        return {
            dateTrunc,
            splitSelect,
            splitGroupBy,
            splitSelectConcatCohortAdsFlow,
            adsFlowSplitSelect,
            adsFlowSplitGroupAlias,
            adsFlowSplitGroupBy,
            cohortSplitSelect,
            cohortSplitGroupAlias,
            cohortSplitGroupBy,
            /** SQL WHERE statement build only from cohort's related dimensions */
            cohortWhere,
            countStarOrDistinctProperId,
            is_in_context_split_ads_flow_specific_columns,
            unnestDatasourceColumn,
            where,
            windowTotal,
            windowRunning,
            join,
        };
    }

    // // compile sql statements specific for this datasource
    // public static compileSQLStatements(
    //     supportedDatasourceColumns: IQueryContextDatasourceColumn[],
    //     queryContextSettings: IQueryContext['settings'],
    //     options: { gcpProjectId: GcpProjectId }
    // ) {
    //     const dateTrunc = queryContextSettings.dateAggregation
    //         ? sqlDateTrunc('date', queryContextSettings.dateAggregation)
    //         : `date`;

    //     // if app_version is present in QueryContext
    //     const is_in_context_app_version =
    //         supportedDatasourceColumns.findIndex(
    //             (dsCol) => dsCol.id === 'ACTIVITY_APP_VERSION'
    //         ) != -1;

    //     // if segment_container_id is present in splits or filters
    //     const is_in_context_segment =
    //         supportedDatasourceColumns.findIndex(
    //             (dsCol) => dsCol.id === 'SEGMENT'
    //         ) != -1;

    //     const sqlTokensArray: Partial<ICompiledSQLTokens>[] =
    //         supportedDatasourceColumns.map((dsCol) => {
    //             return this.sqlMap.columns[dsCol.id]!.compileSQLTokens!(
    //                 dsCol.state,
    //                 options
    //             );
    //         });

    //     // merge tokens into single deduplicated object of type ICompiledSQLTokens
    //     const sqlTokens: ICompiledSQLTokens =
    //         mergeSqlTokensArray(sqlTokensArray);

    //     // unnest app_version_activity if app_version is present in splits or filters
    //     // TODO to powinno być rozwiązane na poziomie datasource, gdzie compileSQL zwróci atrybut join, z treścią `, UNNEST...`

    //     const splitSelect: string = buildSplitGroupSQL(sqlTokens.split_group);
    //     const splitGroupBy = splitSelect ? 'split_group, ' : '';
    //     const countStarOrDistinctProperId = splitSelect
    //         ? 'COUNT(DISTINCT proper_id)'
    //         : 'COUNT(*)';
    //     const unnestDatasourceColumn =
    //         sqlTokens.unnestDatasourceColumn.join('\n');
    //     const where: string = buildWhereSQL(sqlTokens.where);
    //     const join: string = sqlTokens.join.join('\n');
    //     const windowTotal = splitGroupBy ? 'PARTITION BY split_group' : '';
    //     const windowRunning = splitGroupBy
    //         ? 'PARTITION BY split_group ORDER BY day_no'
    //         : 'ORDER BY day_no';

    //     return {
    //         dateTrunc,
    //         splitSelect,
    //         splitGroupBy,
    //         countStarOrDistinctProperId,
    //         is_in_context_app_version,
    //         unnestDatasourceColumn,
    //         where,
    //         windowTotal,
    //         windowRunning,
    //         join,
    //     };
    // }
}
